import React, { useState } from 'react';
import styled from 'styled-components';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { AlertPosition, AlertType, DeviceType, Language, PollQuestionType } from '../../shared/enum/EnumRepository';
import { pollData_1_ko, pollData_1_en } from '../../component/poll/PollData';
import { BlackFullWidthBtn } from '../../shared/component/headless/button/BtnBody';
import { useLoadingStore } from '../../shared/store/loadingStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { PollSelectedValue } from '../../shared/dto/EntityRepository';
import QuestionSectionSingle from '../../component/poll/QuestionSectionSingle';
import { CreatePollReq } from '../../shared/dto/ReqDtoRepository';
import { usePayment } from '../../shared/hooks/useApi/usePayment';
import { useSetting } from '../../shared/hooks/useApi/useSetting';
import { useLanguageStore } from '../../shared/store/languageStore';

export interface PollData {
  [key: string]: PollSelectedValue;
}

const Poll: React.FC = () => {
  const { isLoading } = useLoadingStore();
  const { deviceType } = useDeviceStore();
  const [language, setLanguage] = useState(() => useLanguageStore.getState().language);
  const [pollData, setPollData] = useState<PollData>({});
  const { enqueueAlert } = useStandardAlertStore();
  const { setting } = useSetting();
  const { submitPoll } = usePayment();

  if (!setting){
    return null;
  }

  const handlePollDataChange = (questionId: string, selectedValue: PollSelectedValue) => {
    setPollData(prev => ({
      ...prev,
      [questionId]: selectedValue
    }));
  };

  const scrollToQuestion = (questionId: string) => {
    const questionElement = document.querySelector(`[data-question-id="${questionId}"]`);
    if (questionElement) {
      questionElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  };

  const validatePollData = () => {
    const isKorean = language === Language.KR;
    
    for (const question of currentPollData.questions) {
      const questionId = question.id;
      const questionValue = pollData[questionId];
      const questionNumber = question.title.split('.')[0]; // 질문 번호 추출
      
      // 질문이 답변되지 않은 경우÷
      const isEmpty = questionValue && questionValue.option === "" || 
        (Array.isArray(questionValue) && questionValue.length === 0);
        
      if (isEmpty) {
        const errorMessage = isKorean 
          ? `질문 ${questionNumber}의 답변이 필요합니다.`
          : `Please complete question ${questionNumber}.`;
        return { error: errorMessage, questionId };
      }
      
      // 기타 옵션이 선택된 경우 기타 값이 채워졌는지 확인
      if (questionValue && questionValue.option === 'other' && questionValue.value === "") {
        const errorMessage = isKorean
          ? `질문 ${questionNumber} '기타'의 답변이 필요합니다.`
          : `Please enter details for 'other' option in question ${questionNumber}.`;
        return { error: errorMessage, questionId };
      }
    }
    
    return null;
  };

  const handleSubmit = async () => {    
    // 유효성 검사
    const validationResult = validatePollData();
    if (validationResult) {
      // 에러가 있는 질문으로 스크롤
      scrollToQuestion(validationResult.questionId);
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: validationResult.error,
        callBack: () => {}
      });
      return;
    }
    // TODO: API 호출로 데이터 전송
    const createPollReq: CreatePollReq = {
      version: currentPollData.version,
      userSelected: Object.values(pollData)
    };
    await submitPoll(createPollReq);
  };

  // 언어에 따라 다른 데이터 선택
  const currentPollData = language === Language.KR ? pollData_1_ko : pollData_1_en;

  return (
    <Container $deviceType={deviceType}>
      <LanguageSelector>
        <LanguageOption
          $isActive={language === Language.KR}
          onClick={() => setLanguage(Language.KR)}
        >
          KOR
        </LanguageOption>
        <LanguageDivider>/</LanguageDivider>
        <LanguageOption
          $isActive={language === Language.EN}
          onClick={() => setLanguage(Language.EN)}
        >
          ENG
        </LanguageOption>
      </LanguageSelector>
      <PollTitleContainer>
        <Title>{currentPollData.title}</Title>
        <Subtitle>{currentPollData.subTitle}</Subtitle>
      </PollTitleContainer>
      {currentPollData.questions.map((question) => (
        question.type === PollQuestionType.SINGLE && (
        <QuestionSectionSingle
          key={question.id}
          question={question}
          pollData={pollData}
          onPollDataChange={handlePollDataChange}
          language={language as Language}
        />
        )
      ))}
    <SubmitButton 
      onClick={handleSubmit} 
      disabled={isLoading > 0}
      >
        {currentPollData.submitButton}
    </SubmitButton>
    </Container>
  );
};

export default Poll;

const Container = styled.main<{ $deviceType: DeviceType }>`
  width: ${({ $deviceType }) => $deviceType === DeviceType.MOBILE ? '100%' : $deviceType === DeviceType.TABLET ? '70%' : '45%'};
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: ${({ $deviceType }) => $deviceType === DeviceType.MOBILE ? 'calc(12vh) calc(8vw)' : 'calc(8vh) calc(6vw)'};
  padding-top: ${({ $deviceType }) => $deviceType === DeviceType.MOBILE ? 'calc(12vh)' : 'calc(10vh)'};
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const LanguageSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 16px;
`;

const LanguageOption = styled.button<{ $isActive: boolean }>`
  ${({ theme }) => theme.typography.Footer_01};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.color_Gray_04 : theme.colors.color_Gray_05
  };
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.color_Gray_04};
  }
`;

const LanguageDivider = styled.span`
  ${({ theme }) => theme.typography.Footer_01};
  color: ${({ theme }) => theme.colors.color_Gray_05};
  user-select: none;
`;

const PollTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h1`
  ${({ theme }) => theme.typography.Body_00_1};
  color: ${({ theme }) => theme.colors.color_Gray_01};
  margin: 0;
`;

const Subtitle = styled.p`
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  margin: 0;
`;


const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 10px 0px 20px 0;
`;
